import type { Column } from "@/types/common";
import { renderAutoTooltipCell, shouldAutoTooltip } from "@/components/Tooltip";
import styles from "./SimpleTable.module.scss";

export interface SimpleTableProps<T> {
  columns: Column<T>[];
  data: T[];
  getRowKey: (row: T, index: number) => string | number;
  emptyMessage?: string;
}

// A lightweight, no-toolbar table for a plain preview list (a handful of
// rows inside a card, no search/sort/pagination/export) — see
// `pages/Dashboard/components/UpcomingGatherings` for a worked example.
// For anything needing those, use `components/Table/Table.tsx` instead;
// see docs/coding-practices.md's "Lists / data tables" section.
const SimpleTable = <T,>({ columns, data, getRowKey, emptyMessage = "No data found" }: SimpleTableProps<T>) => (
  <div className={styles.tableWrapper}>
    <table className={styles.table}>
      <thead>
        <tr>
          {columns.map((column, index) => (
            <th key={index} style={{ width: column.width }}>
              {column.name}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.length === 0 ? (
          <tr>
            <td className={styles.emptyCell} colSpan={columns.length}>
              {emptyMessage}
            </td>
          </tr>
        ) : (
          data.map((row, rowIndex) => (
            <tr key={getRowKey(row, rowIndex)}>
              {columns.map((column, columnIndex) => (
                <td key={columnIndex} className={column.className}>
                  {column.cell
                    ? column.cell(row)
                    : shouldAutoTooltip(column)
                      ? renderAutoTooltipCell(column.selector?.(row), {
                          tooltip: column.tooltip,
                          align: column.center ? "center" : undefined,
                        })
                      : column.selector
                        ? String(column.selector(row))
                        : null}
                </td>
              ))}
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

export default SimpleTable;
